import basketUtils from 'utils/Basket';
import orderUtils from 'utils/Order';
import ErrorsUtils from 'utils/Errors';
import ErrorConstants from 'utils/ErrorConstants';
import localeUtils from 'utils/LanguageLocale';
import checkoutApi from 'services/api/checkout';
import locationUtils from 'utils/Location';
import store from 'Store';
import userActions from 'actions/UserActions';
import userUtils from 'utils/User';
import checkoutUtils from 'utils/Checkout';
import { setReady, showModalError } from 'actions/PayPalActions';
import OrderActions from 'actions/OrderActions';
import Actions from 'Actions';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS, SHIPPING_METHODS } from 'constants/Checkout';
import { BASKET_TYPES, FULFILLMENT_TYPES } from 'actions/ActionsConstants';
import PayPalExpressBindings from 'analytics/bindingMethods/components/rwdBasket/PayPalExpressBindings';
import FrictionlessCheckoutUtils from 'utils/FrictionlessCheckout';
import PayPalCheckoutEN from 'utils/locales/PayPalCheckout_en_US';
import PayPalCheckoutFR from 'utils/locales/PayPalCheckout_fr_CA';
import CheckoutEN from 'utils/locales/Checkout_en_US';
import CheckoutFR from 'utils/locales/Checkout_fr_CA';
import Empty from 'constants/empty';
import anaConsts from 'analytics/constants';
import { PAYPAL_WINDOW_CLOSED } from 'constants/PayPal';

const { getLocalisedDataFromResource } = localeUtils;
const getText = getLocalisedDataFromResource(PayPalCheckoutEN, PayPalCheckoutFR);
const getCheckoutText = getLocalisedDataFromResource(CheckoutEN, CheckoutFR);

let paypalCheckoutInstance;
let paypalToken;
let paypalSessionDetails;
let tokenPromise = null;
let paymentGroupPromise = null;
let isSDKLoaded = false;
let sdkLoadPromise = null;
const renderedContainers = {};
const renderInProgress = {};

// PayPal button style constants
const PAYPAL_BUTTON_STYLE = {
    LAYOUT: 'horizontal',
    COLOR: 'gold',
    SHAPE: 'pill',
    HEIGHT: 44
};

const PAYPAL_BUTTON_LABELS = {
    PAYPAL: 'paypal',
    PAY_WITH_PAYPAL: 'pay'
};

// PayPal SDK supported locales for Sephora (US and CA only)
const PAYPAL_SUPPORTED_LOCALES = {
    /* eslint-disable camelcase */
    en_US: 'en_US',
    en_CA: 'en_CA',
    fr_CA: 'fr_CA'
    /* eslint-enable camelcase */
};

const {
    EVENT_NAMES: { PAYPAL_SOT, PAYPAL_EXPRESS, EXPRESS_PAYMENT_SELECTION, CHECKOUT },
    CONTEXT,
    ACTION_INFO
} = anaConsts;

/**
 * Maps the Braintree PayPal tokenize payload into the Sephora CnC address shape.
 *
 * Mirrors the field normalization done by ApplePay's `formatAddress`:
 * uppercases city/country, trims postal code, and strips non-digits from
 * phone before capping at 10 characters. Missing optional fields fall back
 * to empty strings so the backend receives a fully-populated object.
 *
 * @param {object} payload - Braintree PayPal tokenize payload
 * @param {object} [payload.details] - User details returned by PayPal
 * @param {object} [payload.details.shippingAddress] - Shipping address fields (line1, line2, city, state, postalCode, countryCode)
 * @returns {object} Address in the shape expected by `checkoutApi.createShippingAddress`
 */
function formatPayPalShippingAddress(payload) {
    const details = payload?.details || {};
    const shipping = details.shippingAddress || {};
    // Take the last 10 digits so numbers prefixed with a country code (e.g. +1XXXXXXXXXX) still yield the local 10-digit number.
    const normalizePhone = value =>
        String(value ?? '')
            .replace(/\D/g, '')
            .slice(-10);
    // Only accept strings so malformed payloads (objects/numbers/etc.) don't propagate as '[object Object]'.
    const normalizeEmail = value => (typeof value === 'string' ? value.trim() : '');
    const phone = normalizePhone(shipping.phone) || normalizePhone(details.phone);
    const email = normalizeEmail(shipping.email) || normalizeEmail(details.recipientEmail) || normalizeEmail(details.email);

    return {
        firstName: details.firstName || '',
        lastName: details.lastName || '',
        address1: shipping.line1 || '',
        address2: shipping.line2 || '',
        city: (shipping.city || '').toUpperCase(),
        state: shipping.state || '',
        postalCode: (shipping.postalCode || '').trim(),
        country: (shipping.countryCode || '').toUpperCase(),
        phone,
        email
    };
}

const PayPalCheckout = {
    /**
     * Check if PayPal Express (direct order placement) is enabled
     *
     * Determines eligibility based on feature flags and user/fulfillment restrictions.
     * Uses locationUtils.isCheckout() to detect checkout context (any route starting with /checkout,
     * including /checkout/payment, /checkout/confirmation, etc.)
     *
     * Requires ALL of the following:
     * 1. PayPal must be enabled (isPayPalEnabled = true)
     * 2. PayPal Pay Now must be enabled (isPayPalPayNowEnabled.isEnabled = true)
     * 3. Frictionless Checkout R3 or R4 must be enabled (cookie + config)
     * 4. User eligibility (context-aware by page):
     *
     *    When isCheckout() returns true (pathname starts with /checkout):
     *    - Logged-in users: All fulfillment types (STH, SDD, BOPIS)
     *    - Guest users: STH only (SDD and BOPIS excluded)
     *
     *    When on basket page:
     *    - Logged-in users: STH and SDD only (BOPIS excluded)
     *    - Guest users: STH only (SDD and BOPIS excluded)
     *
     * @returns {boolean} True if express flow is enabled and user is eligible
     */
    isPayPalExpressParametersEnabled: function () {
        // PayPal global enable flag (when true, PayPal is enabled)
        const isPayPalEnabled = Sephora.configurationSettings.isPayPalEnabled;

        // PayPal Pay Now flag
        const isPayPalPayNowEnabled = Sephora.configurationSettings.isPayPalPayNowEnabled?.isEnabled;

        // Frictionless Checkout Final release flag (cookie AND config must both be true)
        const isFrictionlessCheckoutEnabled = FrictionlessCheckoutUtils.isFCFinalReleaseEnabled();

        // User authentication and fulfillment type validation
        const isGuest = userUtils.isAnonymous();
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const isCheckoutPage = locationUtils.isCheckout();

        // Context-aware BOPIS eligibility:
        // - Checkout routes (/checkout*): BOPIS allowed for logged-in users
        // - Basket page: BOPIS excluded for all users
        // - Guest users: Only STH allowed (both contexts)
        const isBopisOrder = fulfillmentType === FULFILLMENT_TYPES.PICKUP;
        const isUserEligible = isCheckoutPage
            ? !isGuest || fulfillmentType === FULFILLMENT_TYPES.SHIP // Checkout: Allow BOPIS for logged-in, STH only for guest
            : !isBopisOrder && (!isGuest || fulfillmentType === FULFILLMENT_TYPES.SHIP); // Basket: Exclude BOPIS, STH only for guest

        return isPayPalEnabled && isPayPalPayNowEnabled && isFrictionlessCheckoutEnabled && isUserEligible;
    },
    /**
     * Check if the "Set PayPal as Default Payment" feature is enabled.
     *
     * Controlled by the `isSetPayPalAsDefaultPaymentEnabled` kill switch in
     * configuration. When disabled, the "Set as default payment" checkbox and
     * its supporting copy must be hidden during PayPal selection in checkout.
     *
     * @returns {boolean} True if the feature is enabled
     */
    isSetPayPalAsDefaultPaymentEnabled() {
        return Sephora.configurationSettings?.isSetPayPalAsDefaultPaymentEnabled?.isEnabled === true;
    },
    /**
     * Check if PayPal Checkout (new journey) is enabled for the current locale and fulfillment type.
     *
     * BOPIS context is derived internally from `orderUtils.isBopis()` (on /checkout) or
     * `basketUtils.isPickup()` (on basket) — callers do NOT pass it in.
     *
     * Decision Matrix (per locale - US or CA):
     * | isPayPalPayNowEnabled[US/CA] | isPayPalPayNowOnlyBOPISEnabled | BOPIS | STH/SDD |
     * |------------------------------|--------------------------------|-------|---------|
     * | ON                           | ON                             | New   | Old     |
     * | ON                           | OFF                            | New   | New     |
     * | OFF                          | ON                             | Old   | Old     | (BOPIS flag has no effect)
     * | OFF                          | OFF                            | Old   | Old     |
     *
     * @returns {boolean} True if PayPal Checkout (new journey) should be used
     */
    isPayPalCheckoutEnabledForLocale() {
        const {
            PayPalCheckoutEnabled = false,
            PayPalCheckoutEnabledUS = false,
            PayPalCheckoutEnabledCA = false
        } = Sephora.configurationSettings.payPalConfigurations || {};

        // Master flag check
        const isPayPalPayNowEnabled = Sephora.configurationSettings.isPayPalPayNowEnabled?.isEnabled === true;

        // NEW: Locale-specific PayNow flags
        const isPayPalPayNowEnabledUS = Sephora.configurationSettings.isPayPalPayNowEnabledUS?.isEnabled === true;
        const isPayPalPayNowEnabledCA = Sephora.configurationSettings.isPayPalPayNowEnabledCA?.isEnabled === true;

        // NEW: BOPIS-only restriction flag (when true, restricts new journey to BOPIS only)
        const isPayPalPayNowOnlyBOPISEnabled = Sephora.configurationSettings.isPayPalPayNowOnlyBOPISEnabled?.isEnabled === true;

        // Check locale-specific flags for current user's locale
        let isLocalePayNowEnabled = false;
        let isLocaleCheckoutEnabled = false;
        const isUS = localeUtils.isUS();
        const isCA = localeUtils.isCanada();

        if (isUS) {
            isLocalePayNowEnabled = isPayPalPayNowEnabledUS;
            isLocaleCheckoutEnabled = Boolean(PayPalCheckoutEnabledUS);
        } else if (isCA) {
            isLocalePayNowEnabled = isPayPalPayNowEnabledCA;
            isLocaleCheckoutEnabled = Boolean(PayPalCheckoutEnabledCA);
        }

        // Global gate check
        if (!PayPalCheckoutEnabled || !isPayPalPayNowEnabled) {
            return false;
        }

        // If locale-specific PayNow flag is OFF, return old journey (BOPIS flag has no effect)
        if (!isLocalePayNowEnabled) {
            return false;
        }

        // If locale checkout flag is OFF, return old journey
        if (!isLocaleCheckoutEnabled) {
            return false;
        }

        // BOPIS-only restriction logic
        if (isPayPalPayNowOnlyBOPISEnabled) {
            const isCheckoutPage = locationUtils.isCheckout();
            // When flag is TRUE: restrict new journey to BOPIS only (STH/SDD use old journey)
            const isBopis = isCheckoutPage ? orderUtils.isBopis() : basketUtils.isPickup();

            return isBopis === true;
        }

        // When flag is FALSE: all fulfillment types use new journey
        return true;
    },

    getOrderId: function () {
        if (locationUtils.isCheckout()) {
            return orderUtils.getOrderId();
        }

        // Basket page
        return basketUtils.getOrderId();
    },

    getFulfillmentType: function () {
        const isPickupOrder = basketUtils.isPickup();
        const basket = store.getState().basket;

        if (isPickupOrder) {
            return FULFILLMENT_TYPES.PICKUP;
        }

        // Check for SAMEDAY
        const items = Array.isArray(basket?.itemsByBasket) ? basket.itemsByBasket : Empty.Array;
        const hasSameDayItems = items.some(item => item.basketType === BASKET_TYPES.SAMEDAY_BASKET);

        if (hasSameDayItems) {
            return FULFILLMENT_TYPES.SAMEDAY;
        }

        // Default to SHIP
        return FULFILLMENT_TYPES.SHIP;
    },

    /**
     * Check if basket contains both Same-Day Delivery (SDD) and Ship-to-Home (STH) items
     * Used to determine if address field should be shown for mixed baskets
     * @returns {boolean} True if basket has both SDD and STH items
     */
    hasMixedFulfillmentBasket: function () {
        const basket = store.getState().basket;
        const items = Array.isArray(basket?.itemsByBasket) ? basket.itemsByBasket : Empty.Array;

        const hasSameDayItems = items.some(item => item.basketType === BASKET_TYPES.SAMEDAY_BASKET);
        const hasShipItems = items.some(item => item.basketType === BASKET_TYPES.STANDARD_BASKET);

        return hasSameDayItems && hasShipItems;
    },

    fetchAndSetupPayPalToken: async function () {
        try {
            const orderId = PayPalCheckout.getOrderId();
            const fulfillmentType = PayPalCheckout.getFulfillmentType();
            const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;

            // Build token request options
            const tokenOptions = {
                orderId,
                fulfillmentType
            };

            // If PayPal Express parameters are enabled, include them in token request
            const includeExpressParams = PayPalCheckout.isPayPalExpressParametersEnabled();

            if (includeExpressParams) {
                tokenOptions.isExpressCheckout = true;

                // For SDD orders, also include shipping method for auto-selection
                if (isSddOrder) {
                    tokenOptions.shippingMethodId = SHIPPING_METHODS.SAME_DAY_UNSCHEDULED;
                }
            }

            const { token, paypalSessionDetails: sessionDetails } = await checkoutApi.getPayPalToken(tokenOptions);
            paypalToken = token;
            paypalSessionDetails = sessionDetails;
            await PayPalCheckout.paypalCheckoutSetup({ clientToken: token });
        } catch (errorData) {
            Sephora.logger.error('PayPal: Token fetch failed', {
                error: errorData,
                orderId: PayPalCheckout.getOrderId()
            });
            ErrorsUtils.collectAndValidateBackEndErrors(errorData);
            throw errorData;
        } finally {
            tokenPromise = null;
        }
    },

    /**
     * Prepares PayPal Checkout for rendering by ensuring the PayPal SDK/client token is ready.
     *
     * If a PayPal payment group initialization is currently in-flight, this will wait for it
     * to complete before setting up the SDK.
     *
     * @returns {Promise<void>}
     */
    preparePaypalCheckout: async function () {
        // Wait for payment group initialization if in progress
        if (paymentGroupPromise) {
            await paymentGroupPromise;
        }

        await PayPalCheckout.setupPayPalSDK();
    },

    /**
     * Sets up the PayPal SDK without waiting for paymentGroupPromise.
     * Use this internally from `initializePayPalPaymentGroup` to avoid the
     * self-await deadlock that would occur if `preparePaypalCheckout` were
     * called while `paymentGroupPromise` is still the in-flight promise.
     */
    setupPayPalSDK: async function () {
        // If we already have an instance, we're done
        if (paypalCheckoutInstance) {
            return;
        }

        // If we already have a token, just set up the instance
        if (paypalToken) {
            await PayPalCheckout.paypalCheckoutSetup({ clientToken: paypalToken });

            return;
        }

        // If a request is in progress, wait for it
        if (!tokenPromise) {
            tokenPromise = PayPalCheckout.fetchAndSetupPayPalToken();
        }

        await tokenPromise;
    },

    /**
     * Initialize PayPal payment group when PayPal radio button is selected
     * This follows the same pattern as Venmo, Klarna, Afterpay, and Paze
     * Note: Does not throw - errors are logged and interstice is hidden, but method completes silently
     * to avoid breaking existing call sites that don't handle errors.
     */
    initializePayPalPaymentGroup: async function () {
        // If a payment group initialization is already in progress, wait for it
        if (paymentGroupPromise) {
            await paymentGroupPromise;

            return;
        }

        // PayPal initialization (new journey) initializes the PayPal payment group on the order
        // and ensures the PayPal SDK/client token is ready for rendering the PayPal sheet.
        // Note: the payment group is not complete until the user approves the payment in the PayPal sheet.
        paymentGroupPromise = (async () => {
            try {
                await decorators.withInterstice(async () => {
                    // This function should ONLY be called for the NEW journey
                    // Components must check isPayPalCheckoutEnabledForLocale() before calling
                    await checkoutApi.initializePaymentGroup({ paymentGroupType: orderUtils.PAYMENT_GROUP_TYPE.PAYPAL });
                }, INTERSTICE_DELAY_MS)();

                const order = await checkoutApi.getOrderDetails(PayPalCheckout.getOrderId());
                store.dispatch(OrderActions.updateOrder(order));
                // Use setupPayPalSDK (not preparePaypalCheckout) to avoid awaiting the in-flight
                // paymentGroupPromise we are currently inside, which would deadlock.
                await PayPalCheckout.setupPayPalSDK();
            } catch (err) {
                store.dispatch(Actions.showInterstice(false));
                // Use PayPalCheckout.getOrderId() so the logged orderId reflects checkout vs basket context
                Sephora.logger.error('PayPal: Failed to initialize PayPal payment group', {
                    error: err,
                    orderId: PayPalCheckout.getOrderId(),
                    message: err?.message || 'Unknown error'
                });
                // Do not rethrow - existing call sites don't handle errors
            } finally {
                paymentGroupPromise = null;
            }
        })();

        await paymentGroupPromise;
    },

    /**
     * Returns the PayPal payment amount derived from the initialized order's PayPal price.
     *
     * Return shape:
     * - `totalShipping` — numeric shipping cost. `basketUtils.removeCurrency` parses
     *   currency strings and also normalizes the semantic-zero token `'FREE'` to `0`
     *   for free-shipping orders. Always a finite number; falls back to `0` when
     *   parsing fails.
     * - `amountWithCurrency` — upstream display string passed through as-is from the
     *   backend (e.g., `'$10.00'`, `'$0.00'`, `'FREE'`, `null`). No client-side
     *   validation is applied; the value is only used as the display suffix in
     *   `'Estimated Total: <amountWithCurrency>'` downstream, so the backend is the
     *   source of truth for what's renderable. Consumers concatenating it into a
     *   string MUST guard for the absent case (truthy check or `?? ''` coalesce).
     * - `amount` — numeric equivalent parsed via `basketUtils.removeCurrency` (e.g.,
     *   `10.00` from `'$10.00'`). Falls back to `0` for missing or non-numeric
     *   upstream values (`'ABC'`, `'N/A'`, `'FREE'`, `''`) since the field is
     *   consumed as a number by PayPal.
     * - `isPickupOrder` — `true` for BOPIS orders.
     *
     * @returns {{ totalShipping: number, amountWithCurrency: (string|undefined|null), amount: number, isPickupOrder: boolean }}
     */
    getPaymentAmount: function () {
        const totalShipping = store.getState().order?.orderDetails?.priceInfo?.totalShipping;
        const parsedShipping = Number(basketUtils.removeCurrency(totalShipping ?? ''));
        const payPalAmount = orderUtils.getPayPalAmount();
        const parsedAmount = Number(basketUtils.removeCurrency(payPalAmount ?? ''));

        return {
            totalShipping: Number.isFinite(parsedShipping) ? parsedShipping : 0,
            amountWithCurrency: payPalAmount,
            amount: Number.isFinite(parsedAmount) ? parsedAmount : 0,
            isPickupOrder: basketUtils.isPickup()
        };
    },

    getPaymentConfig: function () {
        const { payPalnonVaulting = false } = Sephora.configurationSettings;
        const currency = localeUtils.isCanada() ? 'CAD' : 'USD';
        const flow = payPalnonVaulting ? 'checkout' : 'vault';

        return {
            payPalnonVaulting,
            currency,
            flow
        };
    },

    /**
     * Get shipping options from shipping methods API
     * Calls /api/checkout/orders/{orderId}/shippingGroups/{groupId}/shippingMethods
     * @returns {Promise<Array>} Promise resolving to array of PayPal shipping options
     */
    getShippingOptionsFromAPI: async function () {
        try {
            const orderId = PayPalCheckout.getOrderId();
            const fulfillmentType = PayPalCheckout.getFulfillmentType();

            // Skip API call for BOPIS (no shipping methods needed for pickup)
            if (fulfillmentType === FULFILLMENT_TYPES.PICKUP) {
                return [];
            }

            const shippingGroupId = PayPalCheckout.getShippingGroupIdForFulfillmentType();

            // Treat any nullish shippingGroupId as missing (handles null and undefined from optional chaining)
            if (!orderId || shippingGroupId == null) {
                return [];
            }

            // Call shipping methods API using existing checkout API function
            const response = await checkoutApi.getAvailableShippingMethods(orderId, shippingGroupId);

            // Extract shippingMethods array from response
            const shippingMethods = response?.shippingMethods;

            if (!shippingMethods || !Array.isArray(shippingMethods)) {
                return [];
            }

            // Get current shipping method ID from order to mark it as selected
            const orderDetails = store.getState().order?.orderDetails;
            const currentShippingGroup =
                fulfillmentType === FULFILLMENT_TYPES.SAMEDAY
                    ? orderUtils.getSameDayShippingGroup(orderDetails)
                    : orderUtils.getHardGoodShippingGroup(orderDetails);
            const currentShippingMethodId = currentShippingGroup?.shippingMethod?.shippingMethodId;

            // Transform shipping methods to PayPal format
            const currency = localeUtils.isCanada() ? localeUtils.ISO_CURRENCY['CA'] : localeUtils.ISO_CURRENCY['US'];
            const shippingOptions = shippingMethods.map((method, index) => {
                // Parse shippingFee and normalize non-numeric values (e.g., "FREE") to "0.00"
                let feeValue = method.shippingFee ? method.shippingFee.replace(/[^0-9.]/g, '') : '';
                feeValue = feeValue || '0.00';

                // Check if shipping is free (0 or 0.00)
                const isFreeShipping = parseFloat(feeValue) === 0;

                // Build label based on fulfillment type:
                // - SDD: shippingMethodType + sameDayTitle + sameDayDeliveryLabel
                // - STH: shippingMethodType + promiseDateRangeLabel + shippingMethodDescription
                let label;
                const shippingMethodType = method.shippingMethodType || '';

                if (fulfillmentType === FULFILLMENT_TYPES.SAMEDAY) {
                    // SDD: Combine shippingMethodType + sameDayTitle + sameDayDeliveryLabel
                    const sameDayTitle = method.sameDayTitle || '';
                    const sameDayDeliveryLabel = method.sameDayDeliveryLabel || '';
                    label = `${shippingMethodType} ${sameDayTitle} ${sameDayDeliveryLabel}`;
                } else {
                    // STH: Combine shippingMethodType + promiseDateRangeLabel + shippingMethodDescription
                    const promiseDateRangeLabel = method.promiseDateRangeLabel || '';
                    const shippingMethodDescription = method.shippingMethodDescription || '';
                    label = `${shippingMethodType} ${promiseDateRangeLabel} ${shippingMethodDescription}`;
                }

                // Use localized free shipping label from Checkout locale resources (e.g., "FREE" in EN, "GRATUIT" in FR-CA)
                const displayLabel = isFreeShipping ? `${label} - ${getCheckoutText('free')}` : label;

                return {
                    id: method.shippingMethodId || `shipping-${index}`,
                    label: displayLabel,
                    // Select the current shipping method if it matches, otherwise select first option
                    selected: currentShippingMethodId ? method.shippingMethodId === currentShippingMethodId : index === 0,
                    type: 'SHIPPING',
                    amount: {
                        currency,
                        value: feeValue
                    },
                    ...(method.shippingMethodDescription && { detail: method.shippingMethodDescription })
                };
            });

            return shippingOptions;
        } catch (error) {
            // Log error for visibility but return empty array as fallback
            // PayPal sheet will open without shipping options picker
            Sephora.logger.error('PayPal: Error fetching shipping methods', error);

            return [];
        }
    },

    /**
     * Get PayPal SDK locale with fallback for unsupported locales
     * PayPal SDK only supports specific locale combinations.
     * @returns {string} Supported PayPal locale (en_US, en_CA, or fr_CA)
     */
    getPayPalLocale: function () {
        const currentLocale = localeUtils.getCurrentLanguageCountryCode();

        // Return locale if it's in the supported whitelist
        if (PAYPAL_SUPPORTED_LOCALES[currentLocale]) {
            return currentLocale;
        }

        // Fallback to en_CA for Canadian users, en_US for all others
        return localeUtils.isCanada() ? PAYPAL_SUPPORTED_LOCALES.en_CA : PAYPAL_SUPPORTED_LOCALES.en_US;
    },

    shouldSendContactInformation: function () {
        if (userUtils.isAnonymous()) {
            return PayPalCheckout.getFulfillmentType() === FULFILLMENT_TYPES.SHIP;
        }

        return true;
    },

    getContactOverrides: function () {
        if (!PayPalCheckout.shouldSendContactInformation()) {
            return null;
        }

        const isGuest = userUtils.isAnonymous();
        let email;
        let phone;

        if (isGuest) {
            // Guest contact info is captured on the order, not the user profile.
            // Without a recipient PayPal hides the "Pay in 4 / Pay in full" selector.
            const orderDetails = store.getState().order?.orderDetails;
            email = orderDetails?.header?.guestProfile?.email;
            phone = orderUtils.getHardGoodShippingGroup(orderDetails)?.address?.phone;
        } else {
            const { primaryPhone } = store.getState().user || Empty.Object;
            email = userUtils.getProfileEmail();
            // Replacing 'phone = primaryPhone ? primaryPhone : userUtils.getUserPhoneNumber();',
            // because paypal needs only primary account associated phone number.
            // Alternatively in absence of 'primaryPhone' Paypal will provide phone number.
            phone = primaryPhone;
        }

        const recipient = {
            ...(email && { recipientEmail: email }),
            ...(phone && {
                internationalPhone: {
                    countryCode: '1',
                    nationalNumber: String(phone).replace(/\D/g, '')
                }
            })
        };

        if (Object.keys(recipient).length === 0) {
            return isGuest ? { contactPreference: 'UPDATE_CONTACT_INFO' } : null;
        }

        return {
            contactPreference: isGuest ? 'UPDATE_CONTACT_INFO' : 'RETAIN_CONTACT_INFO',
            recipient
        };
    },

    loadPayPalSDK: async function () {
        // If SDK is already loaded, we're done
        if (isSDKLoaded) {
            return;
        }

        // If SDK is currently loading, wait for it
        if (sdkLoadPromise) {
            await sdkLoadPromise;

            return;
        }

        // Start loading the SDK
        const { payPalnonVaulting, currency } = PayPalCheckout.getPaymentConfig();
        const locale = PayPalCheckout.getPayPalLocale();

        sdkLoadPromise = paypalCheckoutInstance
            .loadPayPalSDK({
                vault: !payPalnonVaulting,
                currency: currency,
                locale: locale,
                intent: payPalnonVaulting ? 'capture' : 'tokenize'
            })
            .then(() => {
                isSDKLoaded = true;
                sdkLoadPromise = null;
            })
            .catch(err => {
                sdkLoadPromise = null;
                throw err;
            });

        await sdkLoadPromise;
    },

    getShippingGroupIdForFulfillmentType: function () {
        const orderDetails = store.getState().order?.orderDetails;
        const fulfillmentType = PayPalCheckout.getFulfillmentType();

        if (fulfillmentType === FULFILLMENT_TYPES.SAMEDAY) {
            return orderUtils.getSameDayShippingGroup(orderDetails)?.shippingGroupId;
        }

        // SHIP and PICKUP both resolve to the HardGoodShippingGroup; PICKUP just has a store address.
        return orderUtils.getHardGoodShippingGroup(orderDetails)?.shippingGroupId;
    },

    getShippingCallbackURL: function () {
        const { protocol, host } = locationUtils.getLocation();
        const shippingGroupId = PayPalCheckout.getShippingGroupIdForFulfillmentType();
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const sessionSegment = paypalSessionDetails ?? '';

        const params = new URLSearchParams({
            shippingGroupId: shippingGroupId ?? '',
            fulfillmentType: fulfillmentType
        });

        return `${protocol}//${host}/api/checkout/paypal/shipping-callback/${sessionSegment}?${params.toString()}`;
    },

    createPaymentOptions: async function () {
        const paymentAmount = PayPalCheckout.getPaymentAmount();
        const { amountWithCurrency, amount, isPickupOrder } = paymentAmount;
        const { payPalnonVaulting, currency, flow } = PayPalCheckout.getPaymentConfig();
        const contactOverrides = PayPalCheckout.getContactOverrides();
        const hasShippingOverride = isPickupOrder || Boolean(contactOverrides?.recipient);

        // Get shipping options from shipping methods API
        const shippingOptions = await PayPalCheckout.getShippingOptionsFromAPI();

        const options = {
            flow: flow,
            amount: amount,
            currency: currency,
            enableShippingAddress: true,
            shippingAddressEditable: !isPickupOrder,
            shippingOptions: shippingOptions,
            ...(!isPickupOrder && { shippingCallbackUrl: PayPalCheckout.getShippingCallbackURL() }),
            ...(payPalnonVaulting && { intent: 'capture' }),
            ...(contactOverrides?.contactPreference && { contactPreference: contactOverrides.contactPreference }),
            ...(hasShippingOverride && {
                shippingAddressOverride: {
                    ...(isPickupOrder ? basketUtils.shippingAddressOverride() : {}),
                    ...(contactOverrides?.recipient || {})
                }
            })
        };

        if (!payPalnonVaulting) {
            options.billingAgreementDescription = 'Estimated Total: ' + (amountWithCurrency ?? '');
        }

        return options;
    },

    hasShippingGroupForCurrentOrder: function () {
        const orderId = PayPalCheckout.getOrderId();
        const orderDetails = store.getState().order?.orderDetails;
        const headerOrderId = orderDetails?.header?.orderId;

        if (!headerOrderId || headerOrderId !== orderId) {
            return false;
        }

        return Boolean(PayPalCheckout.getShippingGroupIdForFulfillmentType());
    },

    /**
     * Create PayPal order and initialize payment flow
     *
     * Context-aware initialization:
     * - Basket/Product pages (Express checkout): Initializes order with PayPal flow flags via API-only calls
     * - Checkout page: Skips initialization (order already initialized from previous steps)
     *
     * CRITICAL: Uses API-only initialization (checkoutApi.initializeCheckout / initializeAnonymousCheckout)
     * to avoid triggering redirects from checkoutUtils.initializeCheckout, which would interrupt the
     * PayPal SDK order creation flow and prevent the PayPal sheet from opening.
     *
     * Always creates PayPal payment options and triggers Braintree SDK payment creation.
     *
     * @returns {Promise} Promise that resolves when PayPal payment is created
     */
    createOrder: async function () {
        const isCheckoutPage = locationUtils.isCheckout();

        // Skip order initialization when on checkout page - order already initialized
        // Only initialize when on basket/product page (Express checkout flow)
        if (!isCheckoutPage) {
            // Always basket context here (guarded by !isCheckoutPage above)
            const isBopis = basketUtils.isPickup();
            const user = store.getState().user || Empty.Object;
            const isGuestCheckout = userUtils.isAnonymous();
            const isAnonymousCheckout = Boolean(user?.isNewUserFlow);

            // Build API payload for order initialization
            const initPayload = {
                isPaypalFlow: true,
                ...(isBopis && { RopisCheckout: true }),
                ...(isAnonymousCheckout && { email: user.userName }),
                ...(!isAnonymousCheckout && isGuestCheckout && { guestCheckout: true }),
                ...(!isAnonymousCheckout && !isGuestCheckout && { orderId: basketUtils.getOrderId(), profileId: userUtils.getProfileId() })
            };

            // CRITICAL: Use API-only initialization to avoid checkoutUtils.initializeCheckout redirect.
            // checkoutUtils.initializeCheckout calls urlUtils.redirectTo when not on /checkout,
            // which interrupts the PayPal SDK createOrder callback and prevents the PayPal sheet from opening.
            if (isAnonymousCheckout || isGuestCheckout) {
                await checkoutApi.initializeAnonymousCheckout(initPayload);
            } else {
                await checkoutApi.initializeCheckout(initPayload, {});
            }

            // TODO PAYPAL: Remove initializePayPalCheckout call after legacy flow is fully deprecated
            // Legacy flow compatibility - only call initializePayPalCheckout if not using new SDK flow.
            // BOPIS context is derived internally by isPayPalCheckoutEnabledForLocale, no arg needed.
            if (!PayPalCheckout.isPayPalCheckoutEnabledForLocale()) {
                await checkoutApi.initializePayPalCheckout({});
            }

            await checkoutApi.initializePaymentGroup({ paymentGroupType: orderUtils.PAYMENT_GROUP_TYPE.PAYPAL });

            if (!PayPalCheckout.hasShippingGroupForCurrentOrder()) {
                const order = await checkoutApi.getOrderDetails(PayPalCheckout.getOrderId());
                store.dispatch(OrderActions.updateOrder(order));
            }
        }

        // Track PayPal button click analytics
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const pageContext = isCheckoutPage ? CONTEXT.CHECKOUT_PAGE : CONTEXT.BASKET_PAGE;
        const origin = isCheckoutPage ? CHECKOUT.PAYMENT_STEP : PAYPAL_EXPRESS.PAGE_DETAIL.EXPRESS_CHECKOUT;
        const event = isCheckoutPage ? PAYPAL_SOT.PAY_WITH_PAYPAL_CLICK : EXPRESS_PAYMENT_SELECTION;

        PayPalExpressBindings.trackPayPalExpressClick(fulfillmentType, pageContext);

        // TODO: To be removed as part of PAY-6979
        PayPalExpressBindings.trackSOTPayPalClick({
            eventName: event,
            sotType: event,
            pageName: pageContext,
            origin,
            paymentMethod: ACTION_INFO.VENDOR_PAYPAL
        });

        // Create payment options and trigger PayPal SDK
        const paymentOptions = await PayPalCheckout.createPaymentOptions();

        // Create PayPal payment (triggers Braintree SDK)
        return paypalCheckoutInstance.createPayment(paymentOptions);
    },

    onApprove: function (data) {
        return paypalCheckoutInstance.tokenizePayment(data);
    },

    onCancel: function (error) {
        // To follow the same behavior as other payment methods (Venmo)
        // We shouldn't show the error modal at the checkout page
        const isCheckout = locationUtils.isCheckout();

        // PayPal SDK errors expose either `reason` (preferred for human-readable cancel context)
        // or `message`. Compute once and pass as a single value so trackModalError uses it directly.
        const genericErrorMessage = error?.reason || error?.message;

        PayPalExpressBindings.trackModalError({ error, genericErrorMessage });

        if (isCheckout) {
            return;
        }

        // User cancelled PayPal flow - show cancellation message
        store.dispatch(showModalError({ isCancellation: true }));
    },

    onError: function (err) {
        // Paysheet closed before load surfaces as a code-less zoid Error with "window is closed";
        // real SDK errors always carry a `code`, so absence-of-code + that message routes to onCancel.
        const message = (err?.message || '').toLowerCase();
        const isPopupClosed = !err?.code && message.includes(PAYPAL_WINDOW_CLOSED);

        // Route popup-close through onCancel BEFORE dispatching any error modal so checkout-page
        // suppression and the cancellation modal are inherited from onCancel instead of layering
        // the generic error modal on top.
        if (isPopupClosed) {
            Sephora.logger.info('PayPal: paysheet closed before load completed', { errorDetails: err?.message });

            return PayPalCheckout.onCancel(err);
        }

        // Check if this is a tokenization failure (PAY-6417 AC3, AC10)
        const isTokenizationFailure = err?.code === ErrorConstants.ERROR_CODES.PAYPAL_ACCOUNT_TOKENIZATION_FAILED;

        // Show appropriate error modal based on error type
        if (isTokenizationFailure) {
            store.dispatch(showModalError({ isAuthorizationFailure: true }));
        } else {
            // PayPal SDK error - show generic error message
            store.dispatch(showModalError({ isCancellation: false }));
        }

        Sephora.logger.error('PayPal SDK error', {
            err,
            errorCode: err?.code || 'UNKNOWN',
            errorDetails: err?.message
        });

        PayPalExpressBindings.trackModalError({ error: err, genericErrorMessage: err?.message });

        return undefined;
    },

    /**
     * Clean up rendered state for a container (called when component unmounts)
     * @param {object} params - Named parameters
     * @param {string} params.containerId - ID of the container to clean up
     */
    cleanupPayPalButton: function ({ containerId }) {
        delete renderedContainers[containerId];
        delete renderInProgress[containerId];
    },

    /**
     * Handle PayPal button approval
     * @param {object} params - Named parameters
     * @param {object} params.data - PayPal approval data
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     */
    onApproveButton: async function ({ data, isBopis }) {
        const payload = await paypalCheckoutInstance.tokenizePayment(data);

        return PayPalCheckout.handlePayPalCheckoutPayload({ payload, isBopis });
    },

    /**
     * Create PayPal button configuration object
     * @param {object} params - Named parameters
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     * @returns {object} PayPal button configuration
     */
    createPayPalButtonConfig: function ({ isBopis }) {
        const isCheckout = locationUtils.isCheckout();
        const label = isCheckout ? PAYPAL_BUTTON_LABELS.PAY_WITH_PAYPAL : PAYPAL_BUTTON_LABELS.PAYPAL;

        return {
            fundingSource: window.paypal.FUNDING.PAYPAL,
            createOrder: PayPalCheckout.createOrder,
            onApprove: data => PayPalCheckout.onApproveButton({ data, isBopis }),
            onCancel: PayPalCheckout.onCancel,
            onError: PayPalCheckout.onError,
            style: {
                layout: PAYPAL_BUTTON_STYLE.LAYOUT,
                color: PAYPAL_BUTTON_STYLE.COLOR,
                shape: PAYPAL_BUTTON_STYLE.SHAPE,
                label: label,
                disableMaxWidth: true,
                height: PAYPAL_BUTTON_STYLE.HEIGHT
            }
        };
    },

    /**
     * Insert PayPal button iframe into the specified container
     * @param containerId - ID of the container element to render the button in
     * @param isBopis - Whether this is a BOPIS checkout
     */
    insertPayPalButtonIframe: async function ({ containerId, isBopis }) {
        try {
            await PayPalCheckout.loadPayPalSDK();

            const container = document.querySelector(`#${containerId}`);

            if (!container) {
                return;
            }

            container.innerHTML = '';

            const buttonConfig = PayPalCheckout.createPayPalButtonConfig({ isBopis });
            const paypalButton = window.paypal.Buttons(buttonConfig);

            const containerExists = document.querySelector(`#${containerId}`);

            if (!containerExists) {
                return;
            }

            await paypalButton.render(`#${containerId}`);

            // Only mark as rendered if container still exists (component might have unmounted)
            const containerStillExists = document.querySelector(`#${containerId}`);

            if (containerStillExists) {
                renderedContainers[containerId] = true;
            }
        } catch (error) {
            Sephora.logger.error('Error rendering PayPal button:', error);
        } finally {
            delete renderInProgress[containerId];
        }
    },

    /**
     * Render PayPal button to a specific container
     * @param {object} params - Named parameters
     * @param {string} params.containerId - ID of the container element to render the button in
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     */
    renderPayPalButton: async function ({ containerId, isBopis }) {
        if (renderedContainers[containerId]) {
            return;
        }

        if (renderInProgress[containerId]) {
            await renderInProgress[containerId];

            // Check if the render actually succeeded; if not, retry
            if (renderedContainers[containerId]) {
                return;
            }
        }

        if (!paypalCheckoutInstance) {
            Sephora.logger.error('PayPal Checkout instance not initialized');

            return;
        }

        renderInProgress[containerId] = PayPalCheckout.insertPayPalButtonIframe({ containerId, isBopis });
        await renderInProgress[containerId];
    },

    /**
     * Syncs the full shipping address from the PayPal tokenize payload to the order.
     *
     * PayPal's shipping-callback only delivers a partial address (city/state/postalCode/
     * countryCode) for customer-confidentiality reasons. Once the user commits to paying,
     * the complete address arrives in the tokenize payload and must be POSTed via
     * `createShippingAddress` so the order satisfies Sephora CnC's full-address requirement
     * before order placement or the checkout redirect.
     *
     * Skipped intentionally for:
     * - BOPIS orders (no shipping address needed)
     * - Missing shipping group id (unexpected state — logged but does NOT block the order
     *   per product decision; downstream placeOrder will surface the address error if
     *   the partial address from the shipping-callback is still insufficient)
     *
     * @param {object} payload - Braintree PayPal tokenize payload (passed straight through to `formatPayPalShippingAddress`)
     * @returns {Promise<void>} Resolves when the address is synced or the no-op branch returns
     */
    processShippingAddressFromPayload: async function (payload) {
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const isBopis = fulfillmentType === FULFILLMENT_TYPES.PICKUP;

        if (isBopis) {
            return;
        }

        const shippingGroupId = PayPalCheckout.getShippingGroupIdForFulfillmentType();

        // Missing shippingGroupId is unexpected at this point in the flow. Per product decision,
        // we log and continue rather than block checkout — the downstream placeOrder will surface
        // any address-related error if the partial shipping-callback address is insufficient.
        // If product later decides that missing shippingGroupId should block checkout, this
        // log-and-continue behavior will need to be revisited.
        if (!shippingGroupId) {
            Sephora.logger.error('PayPal: Skipping shipping address sync — no shipping group id available', {
                fulfillmentType,
                orderId: PayPalCheckout.getOrderId()
            });

            return;
        }

        await checkoutApi.createShippingAddress({
            shippingGroupId,
            address: formatPayPalShippingAddress(payload),
            saveToProfile: false
        });
    },

    /**
     * Handle PayPal checkout payload after payment approval
     *
     * Determines whether to place order directly (express flow) or redirect to review page (standard flow).
     * Express flow is context- and eligibility-dependent, not just feature-flag gated.
     *
     * Express flow eligibility requires ALL of:
     * - PayPal enabled flag
     * - PayPal Pay Now flag
     * - Frictionless Checkout R3 or R4 enabled (cookie AND config)
     * - User and fulfillment eligibility (context-aware):
     *   - Checkout page: Logged-in users can use all fulfillment types (STH, SDD, BOPIS); guests STH only
     *   - Basket page: Logged-in users can use STH and SDD only (BOPIS excluded); guests STH only
     *
     * @param {object} params - Named parameters
     * @param {object} params.payload - PayPal tokenization payload
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     * @returns {Promise} Resolves when order is placed or checkout is initialized
     */
    handlePayPalCheckoutPayload: async function ({ payload, isBopis }) {
        const PayPalApiError = {
            UNAUTHORIZED: 403,
            INVALID_LOCALE: -10182
        };

        if (isBopis) {
            payload.RopisCheckout = true;
        }

        try {
            // Show interstice when PayPal payment completes and order initialization begins
            store.dispatch(Actions.showInterstice(true));

            // Note: Payment group already initialized before this function is called:
            // - Checkout page: initializePayPalPaymentGroup() called when PayPal radio selected
            // - Basket/product page: createOrder() initializes payment group during Express flow

            // PayPal's shipping-callback only delivers a partial address (city/state/zip/country)
            // for confidentiality reasons. The complete address arrives in the tokenize payload
            // and must be POSTed before placeOrder or the checkout redirect so the order has
            // a complete shipping address (firstName, lastName, address1, phone, etc.).
            await PayPalCheckout.processShippingAddressFromPayload(payload);

            // Send PayPal nonce and payment details to backend
            // The payload contains the Braintree PayPal nonce which is required to complete the transaction
            await checkoutApi.initializePayPalCheckout(payload);

            // Populate user email/phone from PayPal, if empty in store
            const user = store.getState().user;

            if (payload && payload.details) {
                const updates = {};

                if (!user.login && payload.details.email) {
                    updates.login = payload.details.email;
                }

                if (!user.phoneNumber && payload.details.phone) {
                    updates.phoneNumber = payload.details.phone;
                }

                if (Object.keys(updates).length > 0) {
                    store.dispatch(userActions.update(Object.assign({}, user, updates)));
                }
            }

            // Check if user is eligible for PayPal Express (direct order placement)
            // Eligibility respects all feature flags and user/fulfillment restrictions
            const isExpressEligible = PayPalCheckout.isPayPalExpressParametersEnabled();

            // If eligible for express, place order directly and redirect to order confirmation
            if (isExpressEligible) {
                return await PayPalCheckout.placeOrderDirect();
            }

            // Standard flow: redirect to checkout review page
            return PayPalCheckout.goToCheckout({ isBopis });
        } catch (reason) {
            // Hide interstice on error
            store.dispatch(Actions.showInterstice(false));

            // Extract error message
            const errorMessage = reason?.errorMessage || reason?.message || 'PayPal checkout initialization failed';
            const isLocaleOrAuthError = reason.errorCode === PayPalApiError.INVALID_LOCALE || reason.errorCode === PayPalApiError.UNAUTHORIZED;

            Sephora.logger.error('PayPal: Checkout initialization failed', {
                error: reason,
                errorCode: reason?.errorCode,
                errorMessage,
                isLocaleOrAuthError,
                orderId: PayPalCheckout.getOrderId()
            });

            // Show error modal to user with backend error messages
            const errorMessages = reason?.errorMessages || [errorMessage];

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('checkoutErrorTitle'),
                    message: errorMessages.join(' ') || getText('checkoutInitializationFailed'),
                    buttonText: getText('okButton')
                })
            );

            // Return undefined for consistent-return rule
            return undefined;
        }
    },

    goToCheckout: function ({ isBopis }) {
        return checkoutUtils
            .initializeCheckout({
                isPaypalFlow: true,
                ropisCheckout: isBopis
            })
            .then(() => checkoutUtils.initOrderSuccess(true, isBopis))
            .catch(reason => {
                store.dispatch(Actions.showInterstice(false));

                return checkoutUtils.initOrderFailure(reason);
            });
    },

    /**
     * Place order directly from PayPal paysheet (Express checkout flow)
     * Bypasses checkout page and redirects to order confirmation on success
     * Stays on basket page on error
     */
    placeOrderDirect: async function () {
        const orderId = PayPalCheckout.getOrderId();
        const fulfillmentType = PayPalCheckout.getFulfillmentType();

        try {
            // Note: Interstice already shown in handlePayPalCheckoutPayload when PayPal completes
            // Following Venmo pattern - call placeOrder directly without decorators.withInterstice

            // Place order directly - interstice remains visible during API call
            const orderResponse = await checkoutApi.placeOrder({
                orderId,
                originOfOrder: 'web',
                isExpressCheckout: true
            });

            // Fire analytics for successful PayPal Express order placement
            const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;

            if (isSddOrder) {
                PayPalExpressBindings.trackSddExpressOrderPlacement({
                    orderId: orderResponse.orderId || orderId,
                    total: orderResponse.total || orderResponse.orderTotal
                });
            } else {
                PayPalExpressBindings.trackExpressOrderPlacement({
                    orderId: orderResponse.orderId || orderId,
                    total: orderResponse.total || orderResponse.orderTotal,
                    fulfillmentType
                });
            }

            // Use standard placeOrderSuccess flow - handles cleanup and redirect to order confirmation
            checkoutUtils.placeOrderSuccess(orderResponse);

            // Return undefined for consistent-return rule
            return undefined;
        } catch (error) {
            // Order placement failed - stay on basket page
            const errorReason = error?.message || error?.errorMessage || 'Order placement failed';
            const errorCode = error?.errorCode;

            Sephora.logger.error('PayPal Express: Order placement failed, staying on basket page', {
                error,
                errorReason,
                errorCode,
                orderId,
                fulfillmentType
            });

            // Track order placement failure
            const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;

            if (isSddOrder) {
                PayPalExpressBindings.trackSddExpressFallback(errorReason);
            }

            // Hide loading interstice
            store.dispatch(Actions.showInterstice(false));

            // Check if this is a PayPal authorization failure (PAY-6417 AC1, AC2, AC6, AC10)
            // Authorization failures are indicated by specific error codes from Payment Service
            const isAuthorizationFailure =
                errorCode === ErrorConstants.ERROR_CODES.PAYPAL_ACCOUNT_TOKENIZATION_FAILED ||
                error?.key === ErrorConstants.ERROR_CODES.CHECKOUT_GENERIC_ERROR;

            if (isAuthorizationFailure) {
                // Show PayPal authorization error modal (AC3, AC4, AC5)
                store.dispatch(showModalError({ isAuthorizationFailure: true }));
            } else {
                // Show generic error modal for non-authorization errors
                const errorMessages = error?.errorMessages || [errorReason];

                store.dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getText('orderPlacementErrorTitle'),
                        message: errorMessages.join(' ') || getText('orderPlacementFailed'),
                        buttonText: getText('okButton')
                    })
                );
            }

            // Don't throw - allow user to stay on basket page
            return undefined;
        }
    },

    braintreeCreateSuccessCallback: async function ({ clientInstance }) {
        const braintreePayPalCheckout = await import(/* webpackChunkName: "priority" */ 'BraintreePayPalCheckout');

        try {
            const paypalInstance = await braintreePayPalCheckout.create({
                client: clientInstance
            });
            paypalCheckoutInstance = paypalInstance;
            store.dispatch(setReady(true));
        } catch (err) {
            Sephora.logger.error('PayPalCheckout.braintreeCreateSuccessCallback: Error creating Braintree PayPal Checkout', {
                error: err
            });
            throw err;
        }
    },

    paypalCheckoutSetup: async function ({ clientToken }) {
        paypalToken = clientToken;

        const client = await import(/* webpackChunkName: "priority" */ 'BraintreeClient');

        try {
            const clientInstance = await client.create({ authorization: clientToken });
            await PayPalCheckout.braintreeCreateSuccessCallback({ clientInstance });
        } catch (clientErr) {
            Sephora.logger.error('PayPalCheckout.paypalCheckoutSetup: Error creating Braintree Client', {
                error: clientErr,
                hasClientToken: Boolean(clientToken)
            });
            throw clientErr;
        }
    }
};

export default PayPalCheckout;
